import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { DateInput } from "../../components/DateInput/DateInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { NewPartnerForm } from "../../forms/NewPartnerForm/NewPartnerForm";
import { DocumentOfReceive } from "../../models/DocumentOfReceive";
import { Product } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { RoleType } from "../../models/Role";
import { Partner } from "../../models/Partner";
import { MercantileType } from "../../models/ParamSettings";
import { TransactionType } from "../../models/Transaction";
import { getMercantileOutputTemplate } from "../../document-templates/MercantileOutputTemplate";
import { activeUser } from "../../services/AuthService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllPartners } from "../../services/PartnerService";
import { getAllSettings } from "../../services/SettingsService";
import { getOneDocument } from "../../services/NewVersionService";
import "./NewDocumentOfOutputPage.scss";

export interface DocumentOfOutputPageProps {
  show?: string;
}

export const NewDocumentOfOutputPreviewPage = (
  p: DocumentOfOutputPageProps,
) => {
  const { show } = p;

  const { id } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [newDocumentOfOutput, setNewDocumentOfOutput] =
    useState<DocumentOfReceive>({
      id: id ?? "",
      warehouse:
        activeUser?.role?.name === RoleType.Warehouseman
          ? (cloneDeep(activeUser.warehouse) as Warehouse)
          : ({} as Warehouse),
      warehouseman: `${activeUser?.firstName} ${activeUser?.lastName}`,
      type: TransactionType.Export,
      date: new Date(),
      time: new Date(),
      brutto: undefined,
      tara: undefined,
      netto: undefined,
      srps: undefined,
      product: {} as Product,
      productWithCode: "",
      partner: {} as Partner,
      shipper: {} as Partner,
      driverName: "",
      vehicleNumber: "",
      moisture: undefined,
      impurity: undefined,
      breakage: undefined,
      defect: undefined,
      hectoliter: undefined,
      protein: undefined,
      gluten: undefined,
      cellNumberFloor: undefined,
      hybrid: "",
      dryingCosts: undefined,
      dryWeight: undefined,
      shippingSlip: "",
      calculationMethod: true,
      foreignProduct: false,
      productOwner: {} as Partner,
      note: "",
      finishedDocument: false,
      finishedDocumentFirstTime: false,
    });

  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [partnersApi, setPartnersApi] = useState<Partner[]>([]);
  const [settingsApi, setSettingsApi] = useState<any[]>([]);
  const [processing, setProcessing] = useState<boolean>(false);
  const [isPdfReady, setIsPdfReady] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [pdfData, setPdfData] = useState<any>(
    getMercantileOutputTemplate(
      undefined,
      true,
      newDocumentOfOutput,
      [],
      TransactionType.Export,
    ),
  );

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);
  const toast = useRef<any>(null);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookupsAndSettings = async () => {
    const [wResp, partnerResp, sResp] = await Promise.all([
      getAllWarehouses(),
      getAllPartners(),
      getAllSettings(),
    ]);

    [wResp, partnerResp, sResp].forEach(ensureSuccess);

    setWarehousesApi(wResp?.data?.result ?? []);
    setPartnersApi(partnerResp?.data?.result ?? []);
    setSettingsApi(sResp?.data?.result ?? []);
  };

  useEffect(() => {
    setProcessing(true);
    refreshLookupsAndSettings()
      .catch((e: any) => {
        toast.current?.show({
          severity: "error",
          detail: e?.message ?? String(t("Greška pri učitavanju podataka")),
          life: 3500,
        });
      })
      .finally(() => setProcessing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id) {
      setProcessing(true);
      setProcessing(true);
      setIsPdfReady(false);
      getOneDocument(id)
        .then((resp: any) => {
          ensureSuccess(resp);
          const document = resp?.data?.result;
          if (!document) return;

          if (document.date) document.date = new Date(document.date);
          if (document.time) document.time = new Date(document.time);
          if (!document.warehouseman) {
            document.warehouseman = `${activeUser?.firstName} ${activeUser?.lastName}`;
          }
          setNewDocumentOfOutput(document);
          setIsPdfReady(true);
        })
        .catch((e: any) => {
          toast.current?.show({
            severity: "error",
            detail: e?.message ?? String(t("Greška pri učitavanju dokumenta")),
            life: 3500,
          });
        })
        .finally(() => setProcessing(false));
    }
  }, [id]);

  // populate pdf when data changes
  useEffect(() => {
    let categoryType: MercantileType = MercantileType.Wheat;
    if (newDocumentOfOutput.product?.category?.name === "Uljana repica")
      categoryType = MercantileType.OiledBeet;
    if (newDocumentOfOutput.product?.category?.name === "Ječam")
      categoryType = MercantileType.Barley;
    if (newDocumentOfOutput.product?.category?.name === "Soja")
      categoryType = MercantileType.Soya;
    if (newDocumentOfOutput.product?.category?.name === "Suncokret")
      categoryType = MercantileType.Sunflower;
    if (newDocumentOfOutput.product?.category?.name === "Kukuruz")
      categoryType = MercantileType.Corn;
    if (
      isPdfReady &&
      settingsApi.length > 0 &&
      Object.keys(newDocumentOfOutput).length > 0
    ) {
      const timer = setTimeout(() => {
        const newPdf = getMercantileOutputTemplate(
          categoryType,
          true,
          newDocumentOfOutput,
          settingsApi,
          TransactionType.Export,
        );
        setPdfData(newPdf);
        setShowPdf(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [newDocumentOfOutput, isPdfReady, settingsApi]);

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "all",
  });

  const handleChanges = (prop: string, value: any) => {};

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-truck header-title-icon"
              style={{ fontSize: 18 }}
            ></i>
            <span className="header-title">
              {`${t("Pregled otpremnice broj")}:`}{" "}
              {newDocumentOfOutput?.finishedDocumentNumber ??
                newDocumentOfOutput.finishedDocumentId ??
                ""}
            </span>
          </div>
          <div className="header-buttons">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={"Sve otpremnice"}
              icon="bi bi-arrow-left-square"
              onButtonClick={() => {
                navigate(`/mercantile-output-fast`);
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (
                  newDocumentOfOutput.finishedDocument === true &&
                  newDocumentOfOutput.finishedDocumentId
                ) {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće preuzeti otpremnicu koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (
                  newDocumentOfOutput.finishedDocument === true &&
                  newDocumentOfOutput.finishedDocumentId
                ) {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće odštampati otpremnicu koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="form-body">
            <div className="form-input-fields">
              {activeUser?.role?.name === RoleType.Warehouseman ? (
                <TextInput
                  id="warehouse"
                  value={activeUser?.warehouse?.name ?? ""}
                  onValueChange={handleChanges}
                  labelText={t("Magacin")}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              ) : (
                <DropdownInput
                  id="warehouse"
                  value={newDocumentOfOutput.warehouse.id}
                  onValueChange={handleChanges}
                  optionValue="id"
                  optionLabel="name"
                  options={warehousesApi}
                  filter={false}
                  labelText={t("Magacin")}
                  placeholderText={t("Izaberite iz liste")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              )}
              <DateInput
                id="date"
                value={
                  newDocumentOfOutput?.finishedDocumentFirstTime
                    ? (newDocumentOfOutput.date as Date)
                    : new Date()
                }
                onValueChange={handleChanges}
                labelText={t("Datum")}
                placeholderText={t("Izaberite datum")}
                icon="bi bi-calendar-check"
                maxDate={new Date()}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInput
                id="warehouseman"
                value={newDocumentOfOutput.warehouseman as string}
                onValueChange={handleChanges}
                labelText={t("Magacioner")}
                placeholderText={t("Unesite ime magacionera")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <DateInput
                id="time"
                value={
                  newDocumentOfOutput?.finishedDocumentFirstTime
                    ? (newDocumentOfOutput.time as Date)
                    : new Date()
                }
                onValueChange={handleChanges}
                labelText={t("Vreme")}
                placeholderText={t("Izaberite vreme")}
                timeOnly={true}
                icon="bi bi-clock"
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <DropdownInput
                id="partner"
                value={newDocumentOfOutput.partner?.id as string}
                optionValue="id"
                optionLabel="name"
                onValueChange={handleChanges}
                options={partnersApi}
                filter={true}
                labelText={t("Kupac")}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <div className="new-partner-button">
                {(activeUser?.role?.name === RoleType.Superadmin ||
                  activeUser?.role?.name === RoleType.Administrator ||
                  activeUser?.role?.name === RoleType.Warehouseman) && (
                  <NewPartnerForm
                    buttonLabel={`${t("Dodaj kupca")}`}
                    buttonIcon="bi bi-plus-square"
                    show={show}
                    buttonStyle={{
                      border: "none",
                      background: "rgba(21, 115, 71, 1)",
                      color: "#ffffff",
                      marginRight: "1rem",
                      margin: "0 1rem 0 0",
                    }}
                    buttonNewCityStyle={{
                      border: "none",
                      background: "rgba(21, 115, 71, 1)",
                    }}
                    buttonSaveStyle={{
                      border: "none",
                      background: "rgba(0, 165, 83, 1)",
                    }}
                    buttonInactiveStyle={{
                      border: "none",
                      background: "rgba(108, 117, 125, 1)",
                    }}
                  />
                )}
              </div>
              <DropdownInput
                id="shipper"
                value={newDocumentOfOutput.shipper?.id as string}
                optionValue="id"
                optionLabel="name"
                onValueChange={handleChanges}
                options={partnersApi}
                filter={true}
                labelText={t("Prevoznik")}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInput
                id="driverName"
                value={newDocumentOfOutput.driverName as string}
                onValueChange={handleChanges}
                labelText={t("Ime vozača")}
                placeholderText={t("Unesite ime vozača")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInput
                id="vehicleNumber"
                value={(
                  newDocumentOfOutput.vehicleNumber as string
                )?.toUpperCase()}
                onValueChange={handleChanges}
                labelText={t("Registarski broj vozila")}
                placeholderText={t("Unesite registarski broj")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInputWithIcon
                id="cellNumberFloor"
                value={newDocumentOfOutput.cellNumberFloor as string}
                onValueChange={handleChanges}
                labelText={t("Broj ćelije / podni magacin")}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInput
                id="productCategory"
                value={newDocumentOfOutput.product?.category?.name as string}
                onValueChange={handleChanges}
                labelText={t("Vrsta robe")}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInput
                id="product"
                value={newDocumentOfOutput.product?.name as string}
                onValueChange={handleChanges}
                labelText={t("Naziv robe")}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInputWithIcon
                id="brutto"
                value={(newDocumentOfOutput.brutto as number) ?? ""}
                onValueChange={handleChanges}
                labelText={t("Bruto")}
                icon="bi bi-minecart-loaded"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInputWithIcon
                id="tara"
                value={(newDocumentOfOutput.tara as number) ?? ""}
                onValueChange={handleChanges}
                labelText={t("Tara")}
                icon="bi bi-minecart"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInputWithIcon
                id="moisture"
                value={(newDocumentOfOutput.moisture as number) ?? ""}
                onValueChange={handleChanges}
                labelText={t("Vlaga")}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInputWithIcon
                id="impurity"
                value={newDocumentOfOutput.impurity as number}
                onValueChange={handleChanges}
                labelText={t("Primese")}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              {newDocumentOfOutput?.product?.category?.name === "Kukuruz" && (
                <TextInputWithIcon
                  id="breakage"
                  value={(newDocumentOfOutput.breakage as number) ?? ""}
                  onValueChange={handleChanges}
                  labelText={t("Lom")}
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              )}
              {newDocumentOfOutput?.product?.category?.name === "Kukuruz" && (
                <TextInputWithIcon
                  id="defect"
                  value={(newDocumentOfOutput.defect as number) ?? ""}
                  onValueChange={handleChanges}
                  labelText={t("Defekt")}
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              )}
              {(newDocumentOfOutput?.product?.category?.name === "Pšenica" ||
                newDocumentOfOutput?.product?.category?.name === "Ječam") && (
                <TextInputWithIcon
                  id="hectoliter"
                  value={(newDocumentOfOutput.hectoliter as number) ?? ""}
                  onValueChange={handleChanges}
                  labelText={t("Hektolitar")}
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              )}
              {newDocumentOfOutput?.product?.category?.name === "Pšenica" && (
                <TextInputWithIcon
                  id="protein"
                  value={(newDocumentOfOutput.protein as number) ?? ""}
                  onValueChange={handleChanges}
                  labelText={t("Protein")}
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              )}
              {newDocumentOfOutput?.product?.category?.name === "Ječam" && (
                <div className="empty-field"></div>
              )}
              <RadioButtonInput
                id="foreignProduct"
                value={newDocumentOfOutput.foreignProduct}
                radioButtonValue1={t("DA")}
                radioButtonValue2={t("NE")}
                onValueChange={handleChanges}
                labelText={t("Tuđa roba na skladištenju")}
                control={control}
                errors={errors}
                disabled={true}
              />
              <RadioButtonInput
                id="calculationMethod"
                value={newDocumentOfOutput.calculationMethod}
                radioButtonValue1={t("Svodjenje na SRPS")}
                radioButtonValue2={t("Neto")}
                onValueChange={handleChanges}
                labelText={t("Metod obračuna")}
                control={control}
                errors={errors}
                disabled={true}
              />
              {newDocumentOfOutput.foreignProduct === true && (
                <DropdownInput
                  id="productOwner"
                  value={newDocumentOfOutput.productOwner?.id as string}
                  optionValue="id"
                  optionLabel="name"
                  onValueChange={handleChanges}
                  options={partnersApi}
                  filter={true}
                  labelText={`${t("Vlasnik robe")}`}
                  placeholderText={t("Izaberite iz liste")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              )}
              {newDocumentOfOutput.foreignProduct === true && <div></div>}
              <TextInput
                id="loadingCityName"
                value={newDocumentOfOutput.loadingCityName as string}
                onValueChange={handleChanges}
                labelText={`${t("Mesto istovara")}`}
                placeholderText={t("Unesite mesto istovara")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInput
                id="loadingContact"
                value={newDocumentOfOutput.loadingContact as string}
                onValueChange={handleChanges}
                labelText={`${t("Kontakt na istovaru")}`}
                placeholderText={t("Unesite kontakt na istovaru")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <div className="note-field">
                <TextAreaInput
                  id="note"
                  value={newDocumentOfOutput.note as string}
                  onValueChange={handleChanges}
                  rows={1}
                  cols={50}
                  labelText={t("Napomena")}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              </div>
              <RadioButtonInput
                id="finishedDocument"
                value={newDocumentOfOutput.finishedDocument}
                radioButtonValue1={t("DA")}
                radioButtonValue2={t("NE")}
                onValueChange={handleChanges}
                labelText={t("Zaključena otpremnica")}
                control={control}
                errors={errors}
                disabled={true}
              />
            </div>
            <div className="documents-preview">
              {!showPdf || !pdfData ? (
                <p>Učitavam PDF prikaz...</p>
              ) : (
                <PdfPreview
                  pdfDefinition={pdfData}
                  downloadDocument={downloadPdfDocument}
                  printDocument={printPdfDocument}
                  documentName={`Otpremnica_${
                    newDocumentOfOutput.finishedDocumentId ?? ""
                  }`}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
